import { useEffect, useRef, useState } from "react";
import "@/App.css";
import Lenis from "lenis";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";
import { Starfield } from "@/components/Starfield";
import { IntroGate } from "@/components/IntroGate";
import { CieloRicordi } from "@/components/CieloRicordi";
import { MusicToggle } from "@/components/MusicToggle";
import { StoryControls } from "@/components/StoryControls";
import ModificaPage from "@/components/ModificaPage";
import VisitePage from "@/components/VisitePage";
import API from "@/apiBase";
import { ParlaModal } from "@/components/ParlaModal";
import { Marquee } from "@/components/Marquee";

/* ============================================================
   FOTO, VIDEO E DIDASCALIE: non serve toccare questo file!
   La carrellata raccoglie automaticamente tutto ciò che trova
   nelle cartelle frontend/public/media/tappa1..4, nell'ordine
   e con le frasi scritte nei rispettivi didascalie.txt.
============================================================ */

const WHATSAPP_LINK =
  "https://wa.me/393456035441?text=" +
  encodeURIComponent("Ho visto la pagina... parliamone.");

/* la visita viene annotata una sola volta per apertura */
let visitaRegistrata = false;

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
  }),
};

const FadeUp = ({ children, delay = 0, className = "", ...rest }) => (
  <motion.div
    className={className}
    variants={fadeUp}
    custom={delay}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-70px" }}
    {...rest}
  >
    {children}
  </motion.div>
);

const HeroLine = ({ text, index, start }) => (
  <span className="hero-line-mask">
    <motion.span
      className="hero-line"
      initial={{ y: "115%" }}
      animate={{ y: start ? "0%" : "115%" }}
      transition={{ duration: 1.1, delay: 0.35 + index * 0.14, ease: [0.22, 1, 0.36, 1] }}
    >
      {text}
    </motion.span>
  </span>
);

function App() {
  const isModifica = window.location.pathname.startsWith("/modifica");
  const isVisite = window.location.pathname.startsWith("/visite");
  const [modalOpen, setModalOpen] = useState(false);
  const [entered, setEntered] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const orbY = useTransform(heroProgress, [0, 1], [0, 260]);
  const heroTextY = useTransform(heroProgress, [0, 1], [0, 120]);
  const heroOpacity = useTransform(heroProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    if (isModifica) return;
    const lenis = new Lenis({ duration: 1.0, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, [isModifica]);

  useEffect(() => {
    if (isModifica) return;
    console.log(
      "%c✨ Ehi, curiosone del codice...%c\nHai aperto la console, segno che anche tu ami guardare dietro le quinte.\nProva a digitare: %ccucinaNuovoCapitolo()%c e premi Invio.",
      "color:#e6b96f;font-size:16px;font-weight:bold;",
      "color:#8b93ad;font-size:12px;",
      "color:#d98a9e;font-family:monospace;font-size:13px;font-weight:bold;",
      "color:#8b93ad;font-size:12px;"
    );
    window.cucinaNuovoCapitolo = () => {
      console.log(
        "%c\n🍳 ─── NUOVO CAPITOLO IN PREPARAZIONE ─── 🍳\n\n" +
          "%cAmarti è come cucinare insieme:\n" +
          "si parte dagli ingredienti più semplici,\n" +
          "si aggiunge pazienza a fuoco lento,\n" +
          "e quando qualcosa va storto...\n" +
          "si fa un commit e si ricomincia. Insieme.\n\n" +
          "%cIl nostro codice non ha bug:\n" +
          "ha solo sorprese che non avevamo previsto.\n" +
          "E ogni nuovo capitolo lo scriviamo noi,\n" +
          "riga dopo riga, piatto dopo piatto.\n\n" +
          "%c// return amore.toFixed(∞); ❤\n",
        "color:#e6b96f;font-weight:bold;font-size:14px;",
        "color:#e9e4d8;font-size:13px;line-height:1.8;",
        "color:#d98a9e;font-size:13px;line-height:1.8;",
        "color:#6ee7a8;font-family:monospace;font-size:12px;"
      );
      return "❤️ Capitolo servito. Buon appetito.";
    };
  }, []);

  /* annota la visita (una sola volta per apertura; il proprietario
     può escludere il proprio dispositivo dalla pagina /visite) */
  useEffect(() => {
    if (isModifica || isVisite || visitaRegistrata) return;
    if (localStorage.getItem("nospy") === "1") return;
    visitaRegistrata = true;
    fetch(`${API}/api/visita`, { method: "POST" }).catch(() => {});
  }, [isModifica, isVisite]);

  if (isModifica) {
    return (
      <div className="site" data-testid="modifica-wrapper">
        <ModificaPage />
      </div>
    );
  }

  if (isVisite) {
    return (
      <div className="site" data-testid="visite-wrapper">
        <VisitePage />
      </div>
    );
  }

  return (
    <div className="site" data-testid="love-story-page">
      <IntroGate onStart={() => setEntered(true)} />
      <Starfield />
      <motion.div className="scroll-progress" style={{ scaleX: scrollYProgress }} />
      <MusicToggle />
      <StoryControls />

      {/* ── HERO ── */}
      <header className="hero" ref={heroRef} data-testid="hero-section">
        <motion.div className="hero-orb" style={{ y: orbY }} />
        <motion.div className="hero-content" style={{ y: heroTextY, opacity: heroOpacity }}>
          <motion.p
            className="hero-eyebrow"
            initial={{ opacity: 0 }}
            animate={{ opacity: entered ? 1 : 0 }}
            transition={{ delay: 0.2, duration: 1 }}
          >
            <Sparkles size={14} /> Una storia in quattro capitoli
          </motion.p>
          <h1 className="hero-title" data-testid="hero-title" aria-label="Figli delle Stelle">
            <HeroLine text="Figli" index={0} start={entered} />
            <HeroLine text="delle" index={1} start={entered} />
            <HeroLine text="Stelle" index={2} start={entered} />
          </h1>
          <motion.p
            className="hero-sub"
            initial={{ opacity: 0, y: 24 }}
            animate={entered ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
            transition={{ delay: 1.15, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            Scorri lentamente. Ogni foto è un ricordo,
            <br />
            ogni ricordo è una stella accesa per te.
          </motion.p>
          <motion.p
            className="hero-song-note"
            data-testid="hero-song-note"
            initial={{ opacity: 0 }}
            animate={{ opacity: entered ? 1 : 0 }}
            transition={{ delay: 1.9, duration: 1.4 }}
          >
            <span className="footer-star">✦</span> goditi l'ascolto di questa
            canzone <span className="footer-star">✦</span>
          </motion.p>
        </motion.div>
        <motion.div
          className="hero-scroll-hint"
          data-testid="hero-scroll-hint"
          initial={{ opacity: 0 }}
          animate={{ opacity: entered ? 1 : 0 }}
          transition={{ delay: 1.8, duration: 1 }}
        >
          <span>inizia il viaggio</span>
          <ArrowDown size={16} className="bounce-arrow" />
        </motion.div>
      </header>

      <Marquee />

      {/* ── I RICORDI NEL CIELO ── */}
      <CieloRicordi />

      {/* ── IL PRESENTE ── */}
      <section className="present" data-testid="present-section">
        <FadeUp>
          <p className="present-eyebrow">e poi c'è il presente</p>
        </FadeUp>
        <FadeUp delay={0.1}>
          <h2 className="present-title">Adesso.</h2>
        </FadeUp>
        <FadeUp delay={0.18}>
          <div className="present-text">
            <p>
              Se sei arrivata fin qui, la canzone sta ancora suonando. Queste
              foto non le ho messe in fila per caso: volevo ricordarti da dove
              veniamo, prima di chiederti dove andiamo.
            </p>
            <p>
              Lo so che non tutto è stato semplice. Ci sono state volte in cui
              parlavo più di quanto ascoltassi. Ma ogni foto qui sopra dice la
              stessa cosa: con te, anche il giorno più storto aveva un senso.
            </p>
            <p>
              Non ti chiedo una risposta adesso. Solo di essere arrivata fino
              in fondo — ci sei quasi — e poi, se ti va, c'è una cosa che
              vorrei dirti a voce.
            </p>
          </div>
        </FadeUp>
        <FadeUp delay={0.26}>
          <motion.button
            className="parla-btn"
            data-testid="parliamone-btn"
            onClick={() => setModalOpen(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.96 }}
            transition={{ type: "spring", stiffness: 320, damping: 18 }}
          >
            <span className="parla-btn-shine" />
            Parliamone
          </motion.button>
        </FadeUp>
      </section>

      <footer className="footer">
        <p>
          scritto sotto le stelle, per te <span className="footer-star">✦</span>
        </p>
        <a className="footer-edit" href="/modifica" data-testid="footer-edit-link">
          APRI
        </a>
      </footer>

      <AnimatePresence>
        {modalOpen && (
          <ParlaModal
            whatsappLink={WHATSAPP_LINK}
            onClose={() => setModalOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
